import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import type { ComputedAdditionalCharge } from './types';

interface FeeBreakdownModalProps {
  open: boolean;
  onClose: () => void;
  assocFeeNum: number;
  assocGstNum: number;
  assocGstAmountNum: number;
  totalAssocFee: string;
  platformFeeNum: number;
  platformGstNum: number;
  platformGstAmountNum: number;
  totalPlatformFee: string;
  govtFeeNum: number;
  computedAddlCharges: ComputedAdditionalCharge[];
  grandTotalSubtotal: number;
  grandTotalGst: number;
  grandTotalAmount: number;
}

export function FeeBreakdownModal({
  open,
  onClose,
  assocFeeNum,
  assocGstNum,
  assocGstAmountNum,
  totalAssocFee,
  platformFeeNum,
  platformGstNum,
  platformGstAmountNum,
  totalPlatformFee,
  govtFeeNum,
  computedAddlCharges,
  grandTotalSubtotal,
  grandTotalGst,
  grandTotalAmount,
}: FeeBreakdownModalProps) {
  if (!open) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Fee Breakdown Overview"
      className="max-w-xl"
      footer={
        <Button onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-xs text-gray-600">
          Detailed breakdown of Associate Fee, Platform Fee, Additional Charges, GST amounts, and grand total:
        </p>

        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full min-w-[480px] text-xs text-left">
            <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200">
              <tr>
                <th className="px-3 py-2.5 min-w-[130px]">Fee Type</th>
                <th className="px-3 py-2.5 text-right whitespace-nowrap">Fee (₹)</th>
                <th className="px-3 py-2.5 text-right whitespace-nowrap">GST %</th>
                <th className="px-3 py-2.5 text-right whitespace-nowrap">GST Amt (₹)</th>
                <th className="px-3 py-2.5 text-right font-bold whitespace-nowrap">Total (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-emerald-50/30">
                <td className="px-3 py-2.5 font-medium text-gray-900 whitespace-nowrap">Associate Fee</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">₹{assocFeeNum.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">{assocGstNum}%</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">₹{assocGstAmountNum.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right font-semibold text-emerald-800 whitespace-nowrap">₹{totalAssocFee}</td>
              </tr>

              <tr className="hover:bg-blue-50/30">
                <td className="px-3 py-2.5 font-medium text-gray-900 whitespace-nowrap">Platform Fee</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">₹{platformFeeNum.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">{platformGstNum}%</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">₹{platformGstAmountNum.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right font-semibold text-blue-800 whitespace-nowrap">₹{totalPlatformFee}</td>
              </tr>

              <tr className="hover:bg-amber-50/30">
                <td className="px-3 py-2.5 font-medium text-gray-900 whitespace-nowrap">Government Fee</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">₹{govtFeeNum.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">—</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">₹0.00</td>
                <td className="px-3 py-2.5 text-right font-semibold text-amber-800 whitespace-nowrap">₹{govtFeeNum.toFixed(2)}</td>
              </tr>

              {computedAddlCharges.length > 0 ? (
                computedAddlCharges.map((chg, idx) => (
                  <tr key={chg.id || idx} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900 min-w-[130px] max-w-[170px]">
                      <div className="whitespace-nowrap">Additional Charge</div>
                      {chg.name ? (
                        <div className="text-[10px] font-normal text-gray-500 break-words leading-tight">({chg.name})</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">₹{(parseFloat(chg.amountRupees || '0') || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{chg.gstPercentage}%</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">₹{chg.gstAmount}</td>
                    <td className="px-3 py-2 text-right font-semibold text-brand-800 whitespace-nowrap">₹{chg.totalAmount}</td>
                  </tr>
                ))
              ) : (
                <tr className="text-gray-400">
                  <td className="px-3 py-2.5 whitespace-nowrap">Additional Charges</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">₹0.00</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">—</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">₹0.00</td>
                  <td className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">₹0.00</td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-gray-50/80 font-semibold text-gray-900 border-t-2 border-gray-200">
              <tr>
                <td className="px-3 py-2.5 whitespace-nowrap">Overall Total</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">₹{grandTotalSubtotal.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">—</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">₹{grandTotalGst.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right text-sm font-bold text-brand-800 whitespace-nowrap">₹{grandTotalAmount.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </Dialog>
  );
}
